import { useFormikContext } from 'formik';
import { groupBy, has } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type InsurerFormValues } from '.';
import { type InsurerDetailsDataFragment } from '../../../../api/fragments/InsurerDetailsData';
import { type InsuranceProductType } from '../../../../api/types';
import TransferField from '../../../../components/fields/TransferField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import renderMarkdown from '../../../../utilities/renderMarkdown';
import useTranslatedString from '../../../../utilities/useTranslatedString';
import useStaticInsuranceProductType from '../../../shared/staticInsuranceProductType';

export type InsuranceProductRecordType = {
    key: string;
    title: string;
};

const filterOption = (inputValue: string, item: InsuranceProductRecordType) =>
    item.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;

type InsuranceProductTypeFormProps = {
    insurer?: InsurerDetailsDataFragment;
    isUpdate: boolean;
    disabled?: boolean;
};

const InsuranceProductTypeForm = ({ insurer, isUpdate, disabled = false }: InsuranceProductTypeFormProps) => {
    const { t } = useTranslation('insuranceProductDetails');
    const translated = useTranslatedString();
    const {
        values: { insuranceProductTypes: selectedKeys },
    } = useFormikContext<InsurerFormValues>();

    const { insuranceProductTypeOptions } = useStaticInsuranceProductType();

    const insuranceProducts = useMemo(() => {
        if (isUpdate && insurer?.insuranceProducts.length) {
            return groupBy('type', insurer?.insuranceProducts);
        }

        return {};
    }, [insurer?.insuranceProducts, isUpdate]);

    const availableInsuranceProductType = useMemo(() => {
        if (isUpdate) {
            const usedInsuranceProductTypes = selectedKeys.filter((selectedInsuranceProduct: InsuranceProductType) =>
                has(selectedInsuranceProduct, insuranceProducts)
            );

            if (usedInsuranceProductTypes.length) {
                const disabledUsedInsuranceProductTypes = insuranceProductTypeOptions.map(insuranceProductOption => {
                    if (usedInsuranceProductTypes.includes(insuranceProductOption.key)) {
                        return {
                            ...insuranceProductOption,
                            title: `${insuranceProductOption.title}*`,
                            disabled: true,
                        };
                    }

                    return insuranceProductOption;
                });

                return disabledUsedInsuranceProductTypes;
            }
        }

        return insuranceProductTypeOptions;
    }, [selectedKeys]);

    const usedInsuranceProductTypeCopywriting = useMemo(() => {
        if (isUpdate) {
            const copywritingArr = [];
            Object.keys(insuranceProducts).forEach(key => {
                const usedIP = key;
                const usedIn = insuranceProducts[key].map(ip => translated(ip.legalName));
                const insuranceProductType = t(
                    `insuranceProductDetails:types.${usedIP.charAt(0).toLowerCase() + usedIP.slice(1)}`
                );
                const insuranceProductList = usedIn.join(',');
                copywritingArr.push(
                    t('insuranceProductDetails:usedInsuranceProductType.descriptionNotes', {
                        insuranceProductType,
                        insuranceProductList,
                    })
                );
            });

            return copywritingArr;
        }

        return null;
    }, [insuranceProducts, isUpdate, t, translated]);

    return (
        <CollapsibleWrapper defaultActiveKey={t('insuranceProductDetails:cardTitles.insuranceProductType')}>
            <Panel
                key={t('insuranceProductDetails:cardTitles.insuranceProductType')}
                header={t('insuranceProductDetails:cardTitles.insuranceProductType')}
            >
                <TransferField
                    dataSource={availableInsuranceProductType}
                    disabled={disabled}
                    filterOption={filterOption}
                    listStyle={{ width: '100%' }}
                    name="insuranceProductTypes"
                    render={item => item.title}
                    titles={[
                        t('insuranceProductDetails:subTitles.productTypePool'),
                        t('insuranceProductDetails:subTitles.selectedProductType'),
                    ]}
                    required
                    showSearch
                />

                {isUpdate && usedInsuranceProductTypeCopywriting?.length > 0 && (
                    <div style={{ fontSize: '10px', color: '#9c9c9c', lineHeight: '5px', margin: 0 }}>
                        <p>{t('insuranceProductDetails:usedInsuranceProductType.note')}</p>
                        {usedInsuranceProductTypeCopywriting.map(note => (
                            <p>-{renderMarkdown(note)}</p>
                        ))}
                    </div>
                )}
            </Panel>
        </CollapsibleWrapper>
    );
};

export default InsuranceProductTypeForm;
